import { useState } from 'react';
import { Stack, Paper, Group, Text, Badge, Button, Modal, TextInput, Checkbox, Select, Loader, Center, ActionIcon, Tooltip, Accordion } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconTrash, IconPlus, IconUser, IconUsers as IconGroup } from '@tabler/icons-react';
import { useProfileTypes, useUserProfiles, ProfileAttribute, EffectiveProfile } from '../hooks/useProfileTypes';

interface UserProfileEditorProps {
  userId: string;
}

const SOURCE_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  schema: { label: 'По умолчанию', color: 'gray', icon: '📋' },
  group: { label: 'От группы', color: 'blue', icon: <IconGroup size={12} /> },
  user: { label: 'Личное', color: 'green', icon: <IconUser size={12} /> },
};

function AttributeRow({ attr, value, source }: { 
  attr: ProfileAttribute; 
  value: unknown; 
  source: string;
}) {
  const sourceInfo = SOURCE_LABELS[source] || SOURCE_LABELS.schema;
  
  const formatValue = (val: unknown): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Да' : 'Нет';
    return String(val);
  };

  return (
    <Group justify="space-between" py={4}>
      <Group gap="xs">
        <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{attr.label}:</Text>
        <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>{formatValue(value)}</Text>
      </Group>
      <Tooltip label={sourceInfo.label}>
        <Badge variant="light" color={sourceInfo.color} leftSection={sourceInfo.icon} style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
          {source}
        </Badge>
      </Tooltip>
    </Group>
  );
}

function ProfileCard({ profile, profileTypes, onEdit, onDelete }: {
  profile: EffectiveProfile;
  profileTypes: any[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const schema = profileTypes.find(pt => pt.id === profile.profile_type);
  
  return (
    <Accordion.Item value={profile.profile_type}>
      <Accordion.Control>
        <Group justify="space-between" pr="md">
          <Group gap="xs">
            <Text fw={600}>{profile.name}</Text>
            <Badge variant="outline" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>{profile.plugin_id}</Badge>
            {profile.has_direct && (
              <Badge color="green" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>Прямой</Badge>
            )}
          </Group>
          <Group gap={4}>
            {profile.sources.slice(0, 2).map((src, i) => (
              <Badge key={i} variant="light" color="blue" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
                {src}
              </Badge>
            ))}
            {profile.sources.length > 2 && (
              <Badge variant="light" color="gray" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
                +{profile.sources.length - 2}
              </Badge>
            )}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          {schema?.attributes.map((attr: ProfileAttribute) => {
            const attrData = profile.attributes[attr.name];
            if (!attrData) return null;
            
            return (
              <AttributeRow
                key={attr.name}
                attr={attr}
                value={attrData.value}
                source={attrData.source}
              />
            );
          })}
          
          <Group justify="flex-end" mt="xs">
            <ActionIcon variant="subtle" onClick={onEdit}>
              <IconEdit size={16} />
            </ActionIcon>
            {profile.has_direct && (
              <ActionIcon variant="subtle" color="red" onClick={onDelete}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function AttributeInput({ attribute, value, onChange, disabled }: { 
  attribute: ProfileAttribute; 
  value: unknown; 
  onChange: (val: unknown) => void;
  disabled?: boolean;
}) {
  switch (attribute.type) {
    case 'boolean':
      return (
        <Checkbox
          label={attribute.label}
          description={attribute.description}
          checked={value as boolean ?? false}
          onChange={(e) => onChange(e.currentTarget.checked)}
          disabled={disabled}
        />
      );
    case 'select':
      return (
        <Select
          label={attribute.label}
          description={attribute.description}
          data={attribute.options || []}
          value={value as string ?? ''}
          onChange={onChange}
          clearable
          disabled={disabled}
        />
      );
    case 'number':
      return (
        <TextInput
          label={attribute.label}
          description={attribute.description}
          type="number"
          value={value as string ?? ''}
          onChange={(e) => onChange(e.currentTarget.value ? Number(e.currentTarget.value) : undefined)}
          disabled={disabled}
        />
      );
    default:
      return (
        <TextInput
          label={attribute.label}
          description={attribute.description}
          value={value as string ?? ''}
          onChange={(e) => onChange(e.currentTarget.value || undefined)}
          disabled={disabled}
        />
      );
  }
}

export function UserProfileEditor({ userId }: UserProfileEditorProps) {
  const { profileTypes, isLoading: typesLoading } = useProfileTypes();
  const { profiles, isLoading: profilesLoading, setProfile, updateProfile, deleteProfile, isSetting, isUpdating } = useUserProfiles(userId);
  
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});

  const linkedTypes = profiles.map(p => p.profile_type);
  const availableTypes = profileTypes.filter(pt => !linkedTypes.includes(pt.id));

  const handleAdd = async () => {
    if (!selectedType) return;
    try {
      await setProfile({ profileType: selectedType, attributes: overrides });
      notifications.show({ title: 'Успех', message: 'Профиль добавлен', color: 'green' });
      setAddModalOpen(false);
      setSelectedType(null);
      setOverrides({});
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось добавить профиль', color: 'red' });
    }
  };

  const handleEdit = (profileType: string) => {
    const profile = profiles.find(p => p.profile_type === profileType);
    if (!profile) return;
    
    // Extract user-level overrides
    const userOverrides: Record<string, unknown> = {};
    Object.entries(profile.attributes).forEach(([key, attr]) => {
      if (attr.source === 'user') {
        userOverrides[key] = attr.value;
      }
    });
    
    setSelectedType(profileType);
    setOverrides(userOverrides);
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedType) return;
    try {
      await updateProfile({ profileType: selectedType, attributes: overrides });
      notifications.show({ title: 'Успех', message: 'Профиль обновлен', color: 'green' });
      setEditModalOpen(false);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось обновить профиль', color: 'red' });
    }
  };

  const handleDelete = async (profileType: string) => {
    try {
      await deleteProfile(profileType);
      notifications.show({ title: 'Успех', message: 'Профиль удален', color: 'green' });
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось удалить профиль', color: 'red' });
    }
  };

  const getSchema = (profileType: string) => profileTypes.find(pt => pt.id === profileType);

  if (typesLoading || profilesLoading) {
    return <Center py="md"><Loader /></Center>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600}>Профили пользователя</Text>
        <Button 
          size="xs" 
          leftSection={<IconPlus size={14} />}
          onClick={() => setAddModalOpen(true)}
          disabled={availableTypes.length === 0}
        >
          Добавить профиль
        </Button>
      </Group>

      {profiles.length === 0 ? (
        <Paper p="md" withBorder>
          <Center>
            <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>Нет профилей</Text>
          </Center>
        </Paper>
      ) : (
        <Accordion>
          {profiles.map(profile => (
            <ProfileCard
              key={profile.profile_type}
              profile={profile}
              profileTypes={profileTypes}
              onEdit={() => handleEdit(profile.profile_type)}
              onDelete={() => handleDelete(profile.profile_type)}
            />
          ))}
        </Accordion>
      )}

      {/* Add Profile Modal */}
      <Modal opened={addModalOpen} onClose={() => setAddModalOpen(false)} title="Добавить профиль">
        <Stack gap="md">
          <Select
            label="Тип профиля"
            placeholder="Выберите тип"
            data={availableTypes.map(pt => ({ value: pt.id, label: `${pt.name} (${pt.plugin_id})` }))}
            value={selectedType}
            onChange={(val) => {
              setSelectedType(val);
              setOverrides({});
            }}
          />
          
          {selectedType && getSchema(selectedType)?.attributes.filter((a: ProfileAttribute) => a.editable).map((attr: ProfileAttribute) => (
            <AttributeInput
              key={attr.name}
              attribute={attr}
              value={overrides[attr.name]}
              onChange={(val) => setOverrides(prev => ({ ...prev, [attr.name]: val }))}
            />
          ))}
          
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddModalOpen(false)}>Отмена</Button>
            <Button onClick={handleAdd} loading={isSetting} disabled={!selectedType}>
              Добавить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title="Редактировать профиль" size="lg">
        <Stack gap="md">
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
            Редактируйте только личные переопределения. Значения от группы показаны для справки.
          </Text>
          
          {selectedType && getSchema(selectedType)?.attributes.map((attr: ProfileAttribute) => {
            const profile = profiles.find(p => p.profile_type === selectedType);
            const attrData = profile?.attributes[attr.name];
            const isFromGroup = attrData?.source === 'group';
            
            return (
              <Stack key={attr.name} gap={2}>
                <AttributeInput
                  attribute={attr}
                  value={overrides[attr.name] ?? attrData?.value}
                  onChange={(val) => setOverrides(prev => ({ ...prev, [attr.name]: val }))}
                  disabled={!attr.editable}
                />
                {isFromGroup && (
                  <Text c="blue" style={{ fontSize: 'var(--font-size-xs)' }}>Унаследовано от группы</Text>
                )}
              </Stack>
            );
          })}
          
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditModalOpen(false)}>Отмена</Button>
            <Button onClick={handleSave} loading={isUpdating}>
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default UserProfileEditor;
